// Single source of truth for agent identity and agent labelling.
//
// Focus shows agents in six places (filter panel, map labels, map popup,
// observation list, observation/track modals, PDF export). Each of those used
// to carry its own fallback chain, which is why the same agent could appear
// under different names depending on where you looked. Everything now routes
// through getAgentIdCandidates / getAgentDisplayName / resolveAgentLabel.
//
// Label precedence, highest first:
//   1. the sensor registration name (entered in Central, shared with Focus)
//   2. agent.agentName / agentName / AgentName / Name / EntityName
//   3. attributes[key="username"].value
//   4. "Old sensor agent N"  (code-like ids only)
//   5. the raw id/name, unchanged
//
// Note that the label is display-only. The value Focus filters on stays the
// facet's `name` - see buildDisplayAgents.

const normalizeName = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase();

const isUuidLikeName = value =>
  /^[a-z]?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    (value || '').toString().trim()
  );

const isLongHexLikeName = value =>
  /^[a-z]?[0-9a-f]{24,}$/i.test((value || '').toString().trim());

const isCodeLikeName = value =>
  isUuidLikeName(value) || isLongHexLikeName(value);

const isNonEmpty = value =>
  value !== undefined && value !== null && String(value).trim() !== '';

const firstNonEmpty = candidates => {
  const match = (candidates || []).find(value => isNonEmpty(value));
  return match !== undefined ? String(match).trim() : '';
};

const getAttributeValue = (item, key) => {
  const attributes = Array.isArray(item && item.attributes)
    ? item.attributes
    : [];
  const normalizedKey = String(key || '')
    .trim()
    .toLowerCase();
  if (!normalizedKey) return '';
  const entry = attributes.find(attribute => {
    const attributeKey = String(attribute && attribute.key ? attribute.key : '')
      .trim()
      .toLowerCase();
    return attributeKey === normalizedKey;
  });
  const value = entry && entry.value;
  return value !== undefined && value !== null ? String(value).trim() : '';
};

const buildOldSensorLabelMap = values => {
  // De-duplicated before numbering: the same agent is usually passed in more
  // than once (name, value and EntityId all point at it), and duplicates in the
  // sorted list used to consume numbers, producing gaps like 2 and 4 with no
  // 1 or 3.
  const keys = Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map(value => normalizeName(value))
        .filter(value => isCodeLikeName(value))
    )
  ).sort();
  return new Map(
    keys.map((key, index) => [key, `Old sensor agent ${index + 1}`])
  );
};

// Every string that could identify this agent, most specific first. Callers
// should not care whether they are holding a facet value, a map feature, a
// track or an observation - the shapes differ, the identity does not.
//
// Trackers are identified by their tracker id, which reaches Focus as the facet
// `name` and as the layer feature's EntityId, so both are candidates and the
// registry lookup below simply tries them in order.
const getAgentIdCandidates = source => {
  if (!source) return [];
  const props = (source && source.properties) || {};
  const nestedAgent = (source && source.agent) || {};
  const propsAgent = (props && props.agent) || {};

  const candidates = [
    // explicit agent identifiers
    nestedAgent.agentId,
    nestedAgent.agentRef,
    propsAgent.agentId,
    propsAgent.agentRef,
    source.agentId,
    source.AgentId,
    source.agentRef,
    props.AgentId,
    props.agentId,
    props.AgentRef,
    props.agentRef,
    // entity identifiers (the Agents layer keys trackers on EntityId)
    props.EntityId,
    source.EntityId,
    source.entityId,
    // facet identity: `name` is the query key, `value` the display value
    source.name,
    source.value,
    // names last - a username is a workable identity for user agents
    nestedAgent.agentName,
    propsAgent.agentName,
    source.agentName,
    source.AgentName,
    props.AgentName,
    props.Name,
    props.EntityName,
    getAttributeValue(source, 'username')
  ];

  const seen = new Set();
  const result = [];
  candidates.forEach(candidate => {
    if (!isNonEmpty(candidate)) return;
    const key = normalizeName(candidate);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(String(candidate).trim());
  });
  return result;
};

const getAgentId = source => getAgentIdCandidates(source)[0] || '';

// The Agents layer keys every agent on EntityId ('A' + the sensor/user id), and
// the agents facet uses exactly that string as its `name`. That is the only
// reliable join between the two - matching on names cannot work, because the
// facet holds ids while the features hold usernames.
const getEntityId = source => {
  const props = (source && source.properties) || {};
  return firstNonEmpty([
    props.EntityId,
    source && source.EntityId,
    source && source.entityId
  ]);
};

// Focus turns a sensor registration into the synthetic username
// `scc-sensor-<registrationId>-user`, which is a reliable marker that an agent
// is a sensor rather than a person, and yields the registration id.
const SENSOR_USERNAME_PATTERN = /^scc-sensor-(.+)-user$/i;

const getSensorRegistrationId = source => {
  const username = getAttributeValue(source, 'username');
  const match = SENSOR_USERNAME_PATTERN.exec(username);
  if (match) return match[1];
  // Agent ids are the registration id with an 'A' entity prefix.
  const entityId = getEntityId(source) || (source && source.name) || '';
  const bare = String(entityId).trim();
  return /^A\d+$/.test(bare) ? bare.slice(1) : '';
};

// Indexes Agents layer features for lookup by EntityId, with a name index kept
// only as a fallback for payloads that carry no EntityId.
const buildAgentFeatureIndex = features => {
  const byId = new Map();
  const byName = new Map();
  (Array.isArray(features) ? features : []).forEach(feature => {
    const props = (feature && feature.properties) || {};
    const id = normalizeName(getEntityId(feature));
    if (id && !byId.has(id)) byId.set(id, feature);
    [props.Name, props.AgentName, props.EntityName].forEach(value => {
      const key = normalizeName(value);
      if (key && !byName.has(key)) byName.set(key, feature);
    });
  });
  return { byId, byName };
};

// Finds the Agents layer feature belonging to a facet value (or any agent-ish
// source). EntityId first, name only as a fallback.
const findAgentFeature = (source, features) => {
  if (!source) return null;
  const index =
    features && features.byId instanceof Map
      ? features
      : buildAgentFeatureIndex(features);

  const idCandidates = [
    getEntityId(source),
    source.name,
    source.value,
    getAgentId(source)
  ];
  for (let i = 0; i < idCandidates.length; i += 1) {
    const key = normalizeName(idCandidates[i]);
    if (key && index.byId.has(key)) return index.byId.get(key);
  }

  const nameCandidates = [source.value, source.displayLabel, source.name];
  for (let i = 0; i < nameCandidates.length; i += 1) {
    const key = normalizeName(nameCandidates[i]);
    if (key && index.byName.has(key)) return index.byName.get(key);
  }
  return null;
};

// The name an Agents layer feature carries. Name (the username, or the sensor
// name once sensors get Agent documents) wins over Description.
const getFeatureDisplayName = feature => {
  const props = (feature && feature.properties) || {};
  return firstNonEmpty([
    props.Name,
    props.AgentName,
    props.EntityName,
    props.Description
  ]);
};

// The agent name carried by a track or observation payload, and nothing else -
// no GeoJSON properties, no username fallback. This is the narrow chain the
// results list, the modals and the PDF export each used to spell out.
const getPayloadAgentName = item => {
  if (!item) return '';
  const agent = (item && item.agent) || {};
  return firstNonEmpty([agent.agentName, item.agentName, item.AgentName]);
};

// The human name carried by the data itself, ignoring any registration.
const getAgentDisplayName = item => {
  if (!item) return '';
  const props = (item && item.properties) || {};
  const propsAgent = (props && props.agent) || {};
  const username = getAttributeValue(item, 'username');
  return firstNonEmpty([
    getPayloadAgentName(item),
    propsAgent.agentName,
    props.Name,
    props.AgentName,
    props.EntityName,
    props.Description,
    // Focus' synthetic sensor username is an id in disguise, never a label.
    SENSOR_USERNAME_PATTERN.test(username) ? '' : username
  ]);
};

// Looks the agent up in the registration cache (see store/agentRegistry).
// `registry` is a plain id -> record map; keys are matched case-insensitively.
const getAgentRegistration = (source, registry) => {
  if (!registry) return null;
  const candidates = getAgentIdCandidates(source);
  for (let i = 0; i < candidates.length; i += 1) {
    const record =
      registry[normalizeName(candidates[i])] || registry[candidates[i]];
    if (record) return record;
  }
  return null;
};

const getRegistrationName = (source, registry) => {
  const record = getAgentRegistration(source, registry);
  return record && isNonEmpty(record.name) ? String(record.name).trim() : '';
};

// The one label function. `oldSensorLabelMap` is optional; pass the map built
// for the whole visible set so numbering is consistent within that set.
const resolveAgentLabel = (source, options = {}) => {
  const { registry = null, oldSensorLabelMap = null, feature = null } = options;

  const registrationName = getRegistrationName(source, registry);
  if (registrationName) return registrationName;

  // A facet value that differs from the query key is a display string the
  // backend chose deliberately, so it outranks the layer feature. When the two
  // are equal the value is just the agent id and carries no information.
  const facetName = source && source.name;
  const facetValue = source && source.value;
  if (
    isNonEmpty(facetValue) &&
    normalizeName(facetValue) !== normalizeName(facetName)
  ) {
    return String(facetValue).trim();
  }

  // The Agents layer feature this agent was joined to, if any. For people this
  // is the username; for sensors it will be the sensor name once they get Agent
  // documents.
  const featureName = getFeatureDisplayName(feature);
  if (featureName) return featureName;

  // `value` covers facet values, where the backend may already send a display
  // string that differs from the query key in `name`.
  const displayName =
    getAgentDisplayName(source) || firstNonEmpty([source && source.value]);
  const candidates = getAgentIdCandidates(source);

  if (oldSensorLabelMap) {
    // Only fall back to a synthetic label when the agent has nothing but a
    // code-like identifier: a real name always wins.
    const hasRealName = isNonEmpty(displayName) && !isCodeLikeName(displayName);
    if (!hasRealName) {
      for (let i = 0; i < candidates.length; i += 1) {
        const label = oldSensorLabelMap.get(normalizeName(candidates[i]));
        if (label) return label;
      }
    }
  }

  if (isNonEmpty(displayName)) return displayName;
  return candidates[0] || '';
};

// Merges the agents facet with the Agents map layer and decorates each entry
// with the label to render.
//
// IMPORTANT: `name` is what ends up in filters.agents.value and is matched by
// exact string equality on the backend. It is never rewritten here - only
// `displayLabel` is. Changing that breaks filtering rather than just labelling.
const buildDisplayAgents = (
  agents,
  showInactiveAgents,
  agentFeatures,
  registry = null
) => {
  const facetAgents = Array.isArray(agents) ? agents : [];
  const layerAgents = Array.isArray(agentFeatures) ? agentFeatures : [];
  const featureIndex = buildAgentFeatureIndex(layerAgents);

  // Which layer features the facet already accounts for - matched on EntityId,
  // so a feature whose Name is a username still matches its facet id.
  const claimedFeatures = new Set();
  facetAgents.forEach(agent => {
    const feature = findAgentFeature(agent, featureIndex);
    if (feature) claimedFeatures.add(feature);
  });

  const extraAgents = layerAgents
    .filter(feature => !claimedFeatures.has(feature))
    .map(feature => {
      const props = feature.properties || {};
      const entityId = getEntityId(feature);
      // The query key has to be what the backend filters on, which is the
      // EntityId - filtering on a username returns nothing.
      const queryValue =
        entityId || props.Name || props.AgentName || props.EntityName;
      return queryValue
        ? {
            name: queryValue,
            value: queryValue,
            count: 0,
            entityId: entityId || ''
          }
        : null;
    })
    .filter(agent => agent);

  const mergedAgents = facetAgents.concat(extraAgents);
  const agentMap = new Map();

  mergedAgents.forEach(agent => {
    const displayValue = agent.value || agent.name;
    const queryValue = agent.name || agent.value;
    const key = normalizeName(displayValue);
    if (!key) return;

    const existing = agentMap.get(key);
    if (!existing) {
      agentMap.set(key, {
        ...agent,
        name: queryValue,
        value: displayValue
      });
      return;
    }

    const nextCount = Math.max(existing.count || 0, agent.count || 0);
    const preferred =
      (agent.count || 0) >= (existing.count || 0) ? agent : existing;
    const preferredDisplayValue =
      preferred.value || preferred.name || displayValue;
    const preferredQueryValue = preferred.name || preferred.value || queryValue;

    agentMap.set(key, {
      ...preferred,
      name: preferredQueryValue,
      value: preferredDisplayValue,
      count: nextCount,
      entityId: existing.entityId || agent.entityId || ''
    });
  });

  const listedAgents = Array.from(agentMap.values()).filter(agent => {
    const displayValue = agent.value || agent.name || '';
    return !!displayValue;
  });

  const filteredAgents = showInactiveAgents
    ? listedAgents
    : listedAgents.filter(agent => agent.count !== 0);

  const oldSensorLabelMap = buildOldSensorLabelMap(
    filteredAgents.reduce(
      (values, agent) =>
        values.concat([agent.value, agent.name, agent.entityId]),
      []
    )
  );

  // Sorted on the label, not on the query key: the key is an agent id, so
  // sorting on it would order the panel by id and look random to the user.
  return filteredAgents
    .map(agent => {
      const feature = findAgentFeature(agent, featureIndex);
      return {
        ...agent,
        displayLabel:
          resolveAgentLabel(agent, { registry, oldSensorLabelMap, feature }) ||
          agent.value ||
          agent.name
      };
    })
    .sort((a, b) =>
      (a.displayLabel || '').localeCompare(b.displayLabel || '', undefined, {
        sensitivity: 'base'
      })
    );
};

// Collects the ids worth resolving against the registration endpoint, from
// either facet values or Agents layer features. Names are deliberately not
// used as lookup keys - the endpoint takes an id.
const collectAgentIds = sources =>
  Array.from(
    (Array.isArray(sources) ? sources : []).reduce((ids, source) => {
      const props = (source && source.properties) || {};
      const nestedAgent = (source && source.agent) || {};
      const identity = firstNonEmpty([
        nestedAgent.agentId,
        props.AgentId,
        props.agentId,
        props.EntityId,
        source && source.agentId,
        source && source.AgentId,
        source && source.entityId,
        source && source.name
      ]);
      if (identity) ids.add(identity);
      return ids;
    }, new Set())
  );

// The /api/crud/Agent/{id} envelope's container shape has not been pinned
// down yet, so read it tolerantly: try the plausible containers, then fall
// back to the search-style extracted.content envelope. The name field itself
// is confirmed - the backend Sensor schema and the Central sensor payload
// both use `name` - so no fallback keys are needed here. Returns null when
// nothing name-like is found, which makes the caller keep today's behaviour
// rather than invent a label.
const NAME_KEYS = ['name'];

const pickString = (object, keys) => {
  if (!object || typeof object !== 'object') return '';
  const match = keys.find(key => isNonEmpty(object[key]));
  return match ? String(object[match]).trim() : '';
};

const parseAgentRegistration = response => {
  if (!response || typeof response !== 'object' || response.isError) {
    return null;
  }

  // envelope.instance.Agent is the documented shape - sensingcluesr's
  // get_agent() (R/agent.R) reads exactly that, and it is the reference
  // implementation for this endpoint. The rest are defensive.
  const envelope = response.envelope || {};
  const instance = envelope.instance || response.instance || {};

  const containers = [
    instance.Agent,
    instance.agent,
    instance,
    response.Agent,
    response.agent,
    response.sensor,
    response.Sensor,
    response.data,
    response
  ];

  // Envelope used by the search endpoints: extracted.content is an array of
  // single-key blocks with no guaranteed ordering.
  const content =
    response.extracted && Array.isArray(response.extracted.content)
      ? response.extracted.content
      : [];
  content.forEach(block => {
    if (block && typeof block === 'object') {
      Object.keys(block).forEach(key => containers.push(block[key]));
    }
  });

  const source = containers.find(
    container => pickString(container, NAME_KEYS) !== ''
  );
  if (!source) return null;

  return {
    name: pickString(source, NAME_KEYS),
    type: pickString(source, ['type', 'agentType', 'tracktype']),
    userName: pickString(source, ['userName', 'username']),
    vendor: pickString(source, ['vendor']),
    fieldPartner: pickString(source, ['fieldPartner']),
    description: pickString(source, ['description'])
  };
};

// A sensor rather than a person. Three signals, in order of reliability:
// the synthetic `scc-sensor-<id>-user` username, the Agents layer EntityType
// ("Cluey User" / "user" for people), and the registration `type`
// ("tracker" on the trace, "vehicle_tracker" on the registration).
const isSensorAgent = source => {
  if (!source) return false;

  const username = getAttributeValue(source, 'username');
  if (SENSOR_USERNAME_PATTERN.test(username)) return true;

  const props = (source && source.properties) || {};
  const entityType = normalizeName(props.EntityType || source.EntityType);
  if (entityType) {
    if (entityType === 'user' || entityType.indexOf('cluey user') >= 0) {
      return false;
    }
    if (
      entityType.indexOf('sensor') >= 0 ||
      entityType.indexOf('tracker') >= 0
    ) {
      return true;
    }
  }

  const type = normalizeName(source.type);
  return type.indexOf('tracker') >= 0 || type.indexOf('sensor') >= 0;
};

// The Mapbox text-field expression for agent labels, in one place so the layer
// definition and the showAgentNames toggle cannot drift apart. DisplayLabel is
// set by the layer reducer; the rest is a safety net for features stored before
// labelling ran.
const AGENT_LABEL_TEXT_FIELD = [
  'coalesce',
  ['get', 'DisplayLabel'],
  ['get', 'Name'],
  ['get', 'AgentName'],
  ['get', 'EntityName'],
  ''
];

// Label for a map feature or popup, reading the pre-resolved DisplayLabel and
// falling back to the data's own name.
const getFeatureAgentLabel = feature => {
  const props = (feature && feature.properties) || {};
  return (
    firstNonEmpty([props.DisplayLabel]) || getAgentDisplayName(feature) || ''
  );
};

export {
  AGENT_LABEL_TEXT_FIELD,
  getFeatureAgentLabel,
  buildDisplayAgents,
  getAgentDisplayName,
  getPayloadAgentName,
  getFeatureDisplayName,
  getAgentId,
  getEntityId,
  getSensorRegistrationId,
  buildAgentFeatureIndex,
  findAgentFeature,
  getAgentIdCandidates,
  getAgentRegistration,
  getRegistrationName,
  resolveAgentLabel,
  collectAgentIds,
  parseAgentRegistration,
  isSensorAgent,
  isCodeLikeName,
  buildOldSensorLabelMap
};
